/**
 * Email-verification interstitial — editorial-warm version.
 *
 * Sits between sign-up and intake until the Firebase user's
 * `emailVerified` flag flips true. Renders inside `<AuthLayout>`'s
 * form column so the chrome (card, background, header, footer) is
 * the layout's job.
 *
 * Phase 8+ (custom action handler at `/verify-email`): clicking the
 * link in the email no longer requires the user to "come back" to
 * this page — the handler page applies the action code and redirects
 * to /intake automatically. This prompt is still useful as:
 *   - A confirmation that the email was sent (and to which address)
 *   - A way to manually re-check verification status if the redirect
 *     didn't land (e.g., user clicked the link in a different browser)
 *   - A resend affordance for stuck users
 */
import { useState } from 'react';
import type { AuthClient } from '@inside-us-aos/firebase-adapter';
import { type User, reload } from 'firebase/auth';

import { reportError, reportEvent } from '../monitoring';
import styles from './EmailVerificationPrompt.module.css';

export interface IEmailVerificationPromptProps {
  authClient: AuthClient;
  user: User;
  onVerified: () => void;
}

export function EmailVerificationPrompt({
  authClient,
  user,
  onVerified,
}: IEmailVerificationPromptProps): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleResend = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setInfo(null);
    reportEvent('auth.verify_email_resend_clicked');
    try {
      await authClient.resendVerificationEmail();
      setInfo('A fresh verification link is on its way.');
      reportEvent('auth.verify_email_resend_succeeded');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not resend the verification email.',
      );
      reportError(err, { kind: 'auth', flow: 'resend_verification' });
    } finally {
      setBusy(false);
    }
  };

  const handleCheck = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setInfo(null);
    reportEvent('auth.verify_email_check_clicked');
    try {
      await reload(user);
      if (user.emailVerified) {
        reportEvent('auth.verify_email_check_verified');
        onVerified();
      } else {
        reportEvent('auth.verify_email_check_still_unverified');
        setError(
          'We still see your email as unverified. Make sure you clicked ' +
            'the link in the most recent verification email.',
        );
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not check verification status.',
      );
      reportError(err, { kind: 'auth', flow: 'verify_email_reload' });
    } finally {
      setBusy(false);
    }
  };

  const handleSignOut = async (): Promise<void> => {
    setBusy(true);
    try {
      await authClient.signOut();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.block}>
      <header className={styles.header}>
        <div className={styles.envelope} aria-hidden="true">
          <span className={styles.envelope__icon}>✉</span>
        </div>
        <h1 className={styles.headline}>
          Check your inbox{' '}
          <em className={styles.headline__accent}>— we just sent a link.</em>
        </h1>
        <p className={styles.subhead}>
          We sent a verification link to{' '}
          <span className={styles.subhead__email}>{user.email}</span>. Click
          it and you'll be redirected to your application automatically.
        </p>
      </header>

      {info ? (
        <div role="status" className={styles.info}>
          {info}
        </div>
      ) : null}
      {error ? (
        <div role="alert" className={styles.error}>
          {error}
        </div>
      ) : null}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primary_cta}
          onClick={() => {
            void handleCheck();
          }}
          disabled={busy}
        >
          I clicked the link
        </button>

        <button
          type="button"
          className={styles.secondary_cta}
          onClick={() => {
            void handleResend();
          }}
          disabled={busy}
        >
          Send it again
        </button>
      </div>

      <div className={styles.footer_row}>
        <span>Wrong email?</span>
        <button
          type="button"
          className={styles.signout_link}
          onClick={() => {
            void handleSignOut();
          }}
          disabled={busy}
        >
          Sign out and start over
        </button>
      </div>

      <p className={styles.tip}>
        <strong>Don't see it?</strong> Check your spam folder. The email
        comes from <code>noreply@app.greenbroad.co</code> and the subject
        is "Verify your email for Greenbroad."
      </p>
    </div>
  );
}
